import React, { useState, useEffect, useContext } from "react";
import { Grid, Button, Typography, Box } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import TenantIssuerIcon from "@material-ui/icons/BrandingWatermark";

import translate from "../i18n/Translator";
import { TenantIssuer, TenantIssuerRequest } from "../model/TenantIssuer";
import { REGIME } from "../model/Cfdi";
import { getTenantIssuer, updateTenantIssuer } from "../api/TenantIssuerAPI";
import ValidatedInput, { InputRef } from "../components/ValidatedInput";
import { RfcValidator } from "../components/Validators";
import UploaderInput from "../components/UploaderInput";
import Surface from "../components/Surface";
import { SuccessSnackbar, ErrorSnackbar } from "../components/Snackbars";
import Progress from "../components/Progress";
import { AppContext } from "../context/AppContext";
import Alert from "../components/Alert";

export default function TenantIssuerForm() {
    const context = useContext(AppContext);
    const theme = useTheme();
    const isXs = useMediaQuery(() => theme.breakpoints.down("xs"));
    const margin = "dense";
    const regimeLabels = REGIME.map(v => translate(`regime.${v}`) as string);
    const rfcValidator = new RfcValidator();

    const [status, setStatus] = useState<string>("loading");
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();

    const [active, setActive] = useState(false);
    const [request, setRequest] = useState<TenantIssuerRequest>({
        name: "",
        rfc: "",
        regime: "",
        certificate_url: "",
        private_key_url: "",
        private_key_password: "",
    } as TenantIssuerRequest);
    const [validations, setValidations] = useState({
        "name": {
            valid: false
        } as InputRef,
        "rfc": {
            valid: false
        } as InputRef,
        "regime": {
            valid: false
        } as InputRef,
        "certificate_url": {
            valid: false
        } as InputRef,
        "private_key_url": {
            valid: false
        } as InputRef,
        "private_key_password": {
            valid: false
        } as InputRef
    } as any);

    const buildRequest = (issuer?: TenantIssuer) => {
        setActive(issuer?.active || false);
        setRequest({
            name: issuer?.name || "",
            rfc: issuer?.rfc || "",
            regime: issuer?.regime || ""
        } as TenantIssuerRequest);
    };

    useEffect(() => {
        setStatus("loading");
        getTenantIssuer(context.session!.tenant!.id)
            .then((issuer) => {
                buildRequest(issuer);
                setStatus("loaded");
            })
            .catch((error) => {
                setStatus(error.message);
            });
        // eslint-disable-next-line
    }, []);

    const hasChanged = (name: string, value: string, inputRef: InputRef) => {
        setRequest({ ...request, [name]: value });
        validations[name] = inputRef;
        setValidations(validations);
    };

    const isValid = () => {
        var valid = true;
        for (let field in validations) {
            let ref = validations[field];
            if (!ref.valid) {
                ref.blurer(true);
                valid = false;
            }
        }
        return valid;
    }

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isValid()) {
            return;
        }

        setSubmitting(true);
        updateTenantIssuer(context.session!.tenant!.id, request).then((issuer) => {
            buildRequest(issuer);
            setSuccess(translate("tenants_issuers.success") as string);
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setSubmitting(false);
        })
    };

    const onClosedSnackbar = () => {
        setSuccess(undefined);
        setError(undefined);
    };

    if (status === "loading") {
        return <Progress />;
    }

    if (status !== "loaded") {
        return (
            <Typography variant="body1" component="h5" color="error" align="center">
                {status}
            </Typography>
        );
    }

    return (
        <Grid item xs={12}>
            <Grid container justify="center" alignItems="center">
                <Grid item xs={12} md={10} lg={7} xl={5}>
                    <Surface title={translate("tenants_issuers.title")} icon={<TenantIssuerIcon />} className="FormSurface">
                        <form autoComplete="off" noValidate onSubmit={onSubmit} >
                            <Grid container justify="space-between" alignItems="center">
                                {active && (
                                    <Grid item xs={12}>
                                        <Alert variant="success" title={translate("tenants_issuers.active")} subheader={translate("tenants_issuers.active_text")} />
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <ValidatedInput type="text" id="name" name="name"
                                        value={request.name} label={translate("tenants_issuers.name") as string}
                                        required disabled={submitting}
                                        margin={margin}
                                        onValueChanged={hasChanged} />
                                </Grid>
                                <Grid item xs={12}>
                                    <ValidatedInput type="text" id="rfc" name="rfc"
                                        value={request.rfc} label={translate("tenants_issuers.rfc") as string}
                                        required disabled={submitting}
                                        margin={margin} validator={rfcValidator}
                                        onValueChanged={hasChanged} />
                                </Grid>
                                <Grid item xs={12}>
                                    <ValidatedInput type="text" id="regime" name="regime"
                                        value={request.regime} label={translate("regime.title") as string}
                                        required disabled={submitting}
                                        options={REGIME} optionLabels={regimeLabels}
                                        margin={margin}
                                        onValueChanged={hasChanged} />
                                </Grid>
                                <Grid item xs={12}>
                                    <UploaderInput id="certificate_url" name="certificate_url"
                                        value={request.certificate_url} label={translate("tenants_issuers.certificate_url") as string}
                                        required disabled={submitting}
                                        margin={margin} acceptExtension=".cer"
                                        onValueChanged={hasChanged} />
                                </Grid>
                                <Grid item xs={12}>
                                    <UploaderInput id="private_key_url" name="private_key_url"
                                        value={request.private_key_url} label={translate("tenants_issuers.private_key_url") as string}
                                        required disabled={submitting}
                                        margin={margin} acceptExtension=".key"
                                        onValueChanged={hasChanged} />
                                </Grid>
                                <Grid item xs={12}>
                                    <ValidatedInput type="password" id="private_key_password" name="private_key_password"
                                        value={request.private_key_password} label={translate("tenants_issuers.private_key_password") as string}
                                        required disabled={submitting}
                                        margin={margin}
                                        onValueChanged={hasChanged} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box pt={2}>
                                        <Grid container justify="flex-start" spacing={1} direction="row-reverse">
                                            <Grid item xs sm="auto">
                                                <Button type="submit" variant="contained" color="primary" size="large" disabled={submitting} fullWidth={isXs}>
                                                    {translate("buttons.save")}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                        <ErrorSnackbar message={error} onClose={onClosedSnackbar} />
                        <SuccessSnackbar message={success} onClose={onClosedSnackbar} />
                    </Surface>
                </Grid>
            </Grid>
        </Grid>
    );
}